import { useState, useEffect } from 'react'
import { FiCopy, FiCheck } from 'react-icons/fi'

// Valor da dívida NA ASSESSORIA (cob.twms), linha por linha, igual à lateral
// de lá — SÓ PARA VER (16/10/2026). O diretor negocia pelo fin e às vezes a
// dívida já está com a assessoria: ele precisa do número exato de lá à mão.
// ⚠️ Nada daqui entra em boleto, parcelamento ou mensagem do fin.
//
// A conta é feita PELO COB (mesmos títulos, fórmula e config salva no servidor
// dele) e chega pronta por /api/assessoria/:codigo. NÃO recalcular aqui: em
// 13/10 duas tentativas de reimplementar deram números que não existiam em
// lugar nenhum. Só aparece quando o cliente está na lista do cob (45+ dias).

type Fetch = (url: string, options?: RequestInit) => Promise<Response>
type Modo = 'assessoria' | 'acordado'
interface Soma { valor: number; correcao: number; juros: number; multa: number; honorarios: number; total: number }
interface Assessoria {
  naAssessoria: boolean; minDias: number; diasMax: number; titulos: number
  config: { cfg: { jurosMensal: number; multa: number; honorarios: number; modo: Modo }; origem: 'padrao' | 'servidor'; por: string | null; em: string | null }
  igpmAte: string | null; semIgpm: boolean
  somas: Record<Modo, Soma>
}
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const pct = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 4 })

export function HonorariosCob({ codigo, authenticatedFetch }: { codigo: string | number | null | undefined; authenticatedFetch: Fetch }) {
  const [dados, setDados] = useState<Assessoria | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [modo, setModo] = useState<Modo>('assessoria')
  const [copiado, setCopiado] = useState(false)

  useEffect(() => {
    setDados(null); setErro(null)
    if (!codigo) return
    let on = true
    authenticatedFetch(`/api/assessoria/${encodeURIComponent(String(codigo))}`)
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Erro ${r.status}`); return d })
      .then((d: Assessoria) => { if (!on) return; setDados(d); setModo(d.config.cfg.modo === 'acordado' ? 'acordado' : 'assessoria') })
      .catch(e => { if (on) setErro(e instanceof Error ? e.message : 'Erro') })
    return () => { on = false }
  }, [codigo, authenticatedFetch])

  if (erro) return (
    <div className="px-4 py-2.5 border-b border-border/50 text-xs text-muted-foreground" title={erro}>
      Valor na assessoria indisponível agora (cob.twms não respondeu).
    </div>
  )
  if (!dados || !dados.naAssessoria) return null

  const { cfg } = dados.config
  const b = dados.somas[modo]
  const linhas: [string, number, string][] = [
    ['Valor (V.D.)', b.valor, ''],
    ...(b.correcao !== 0 ? [['Correção (IGP-M)', b.correcao, 'text-sky-600 dark:text-sky-300'] as [string, number, string]] : []),
    ...(b.juros > 0 ? [['Juros', b.juros, 'text-amber-600 dark:text-amber-300'] as [string, number, string]] : []),
    ...(b.multa > 0 ? [['Multa', b.multa, 'text-orange-600 dark:text-orange-300'] as [string, number, string]] : []),
    ...(b.honorarios > 0 ? [[`Honorários (${pct(cfg.honorarios)}%)`, b.honorarios, 'text-purple-600 dark:text-purple-300'] as [string, number, string]] : []),
  ]
  const regra = `${modo === 'assessoria' ? 'assessoria (IGP-M + compostos)' : 'acordado (simples)'} · juros ${pct(cfg.jurosMensal)}% a.m. · multa ${pct(cfg.multa)}% · honor. ${pct(cfg.honorarios)}%`
  const copiar = async () => {
    const txt = [
      `Valor na assessoria (${modo === 'assessoria' ? 'Assessoria' : 'Acordado'})`,
      ...linhas.map(([r, v]) => `${r}: ${brl(v)}`),
      `Encargos totais: ${brl(b.total - b.valor)}`,
      `Total atualizado: ${brl(b.total)}`,
    ].join('\n')
    try { await navigator.clipboard.writeText(txt); setCopiado(true); setTimeout(() => setCopiado(false), 1500) } catch { /* clipboard bloqueado */ }
  }

  // PLANOS (21/10/2026): seção da lateral do cliente — título de seção 13px,
  // linhas 13px e notas 12px (antes 9–11px), respiro p-4.
  return (
    <div className="border-b border-border/50">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 glass-bar">
        <span className="text-[13px] font-semibold text-foreground"
          title="O mesmo valor que aparece na lateral do cob.twms. Só para consulta: não entra no boleto nem no parcelamento do fin.">
          Valor na assessoria (cob)
        </span>
        <div className="inline-flex items-center rounded-md border border-border overflow-hidden"
          title={'Acordado: juros simples sobre o valor original.\nAssessoria: correção IGP-M + juros compostos + honorários sobre o subtotal.\nAbre no modo que a assessoria usa.'}>
          {(['acordado', 'assessoria'] as Modo[]).map(m => (
            <button key={m} onClick={() => setModo(m)}
              className={`px-2.5 py-1 text-xs transition-colors ${modo === m ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
              {m === 'acordado' ? 'Acordado' : 'Assessoria'}
            </button>
          ))}
        </div>
      </div>
      <div className="px-4 py-3 space-y-1">
        {linhas.map(([rot, v, tom]) => (
          <div key={rot} className="flex items-center justify-between gap-2 text-[13px] tabular-nums">
            <span className="text-muted-foreground">{rot}</span>
            <span className={`shrink-0 ${tom || 'text-foreground'}`}>{brl(v)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between text-[13px] tabular-nums pt-1.5 mt-1 border-t border-dashed border-border/40"
          title="Correção + juros + multa + honorários: tudo que incide sobre a dívida original">
          <span className="text-muted-foreground font-medium">Encargos totais</span>
          <span className="text-foreground font-semibold">{brl(b.total - b.valor)}</span>
        </div>
        <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-border/40 text-sm">
          <span className="font-semibold text-foreground">Total atualizado</span>
          <span className="font-bold text-foreground tabular-nums">{brl(b.total)}</span>
        </div>
        <div className="flex items-start justify-between gap-2 pt-1">
          <span className="text-xs text-muted-foreground leading-snug">
            {regra}
            {dados.semIgpm && modo === 'assessoria' && <span className="block text-amber-600">sem a série IGP-M agora: correção não aplicada</span>}
            {dados.config.origem === 'padrao' && <span className="block">configuração padrão do cob (ninguém salvou ainda)</span>}
          </span>
          <button onClick={copiar} title="Copiar o cálculo"
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground">
            {copiado ? <FiCheck className="w-3 h-3 text-emerald-500" /> : <FiCopy className="w-3 h-3" />}{copiado ? 'copiado' : 'copiar'}
          </button>
        </div>
      </div>
    </div>
  )
}
